import { postCheckout } from '@/api/endpoints/billing'
import { ApiError } from '@/api/errors'
import { BILLING_ERROR_CODES, type WidgetPayload } from '@/api/types'
import { PLAN_ID_FIELD } from '@/constants/pro'
import { openPaymentWidget } from '@/services/cloudPayments'
import { trackPaymentInit, type PaymentSource } from '@/services/metrikaGoals'
import { useBillingStore, type PaymentState } from '@/stores/billing'
import { useCommonStore } from '@/stores/common'
import { usePaywallStore } from '@/stores/paywall'
import { usePlansStore } from '@/stores/plans'
import { useUsageStore } from '@/stores/usage'

/**
 * Единственная точка входа в оплату: заказ, виджет, поллинг.
 */

/**
 * Во сколько раз сумма должна превысить цену, чтобы считаться испорченной.
 * Внешний сервис отдавал 39900 при цене 399 ₽ — виджет ждёт рубли, и такой payload
 * означал бы списание 39 900 ₽ с настоящей карты.
 */
const AMOUNT_SANITY_FACTOR = 50

/**
 * Состояния, из которых можно начать оплату. Всё остальное — оплата в процессе:
 * грузится SDK, открыт виджет, идёт подтверждение или Pro уже включён.
 *
 * unknown в списке намеренно. В нём ничего не исполняется — это тупик, куда фронт
 * попадает, когда исход платежа так и не выяснился, а шторки у него нет. Без права
 * на новый заказ человек остаётся с кнопкой, которая молча не работает до перезагрузки.
 */
const RESTARTABLE_STATES: PaymentState[] = ['idle', 'failed', 'unknown']

/**
 * Сумма выглядит как копейки вместо рублей.
 *
 * Делить на 100 нельзя: в день, когда бэк перейдёт на рубли, фронт спишет 3,99 ₽ и
 * никто этого не заметит. Поэтому не чиним, а отказываемся открывать виджет.
 *
 * База — цена тарифа из /billing/plans (в рублях), а не константа: цену бэк теперь
 * меняет без релиза фронта, и порог обязан ехать вместе с ней.
 */
function isAmountBroken(amount: unknown, priceMajor: number): boolean {
	return typeof amount === 'number' && Number.isFinite(amount) && amount >= priceMajor * AMOUNT_SANITY_FACTOR
}

/**
 * Номер заказа для наблюдения. Единственный источник — invoiceId платёжного payload:
 * именно им бэк ключует GET /billing/orders/{id}. Собственный order_id ответа чекаута
 * для этого не годится и, по договорённости, скоро перестанет приходить вовсе.
 *
 * Второе и последнее место, где фронт читает поле чужого payload по имени (первое —
 * amount ради ассерта суммы). Число принимаем наравне со строкой: идентификатор родом
 * из внешнего сервиса, и его тип за нас никто не гарантировал.
 */
function readInvoiceId(widget: WidgetPayload | undefined): string | null {
	const raw = widget?.invoiceId ?? widget?.externalId

	if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw)
	if (typeof raw !== 'string') return null
	return raw.trim() || null
}

/**
 * Ключ терминала. Приходит от бэка внутри payload; без него виджет не открывается ни
 * одним из своих методов. Имя поля зависит от поколения API виджета, поэтому принимаем
 * оба: классический publicId и intent-овый publicTerminalId.
 */
function hasTerminalKey(widget: WidgetPayload): boolean {
	const key = widget?.publicId ?? widget?.publicTerminalId
	return typeof key === 'string' && key.trim().length > 0
}

export function usePaymentFlow() {
	/**
	 * Идентификатор тарифа для чекаута. Тарифы могли не загрузиться — тогда стор отдаёт
	 * фолбэк с прежним слагом 'pro_monthly', то есть поведение до появления ручки.
	 */
	function resolvePlanId(): string {
		const pro = usePlansStore().pro
		return PLAN_ID_FIELD === 'name' ? pro.name : pro.slug
	}

	async function startCheckout(source: PaymentSource): Promise<void> {
		const billing = useBillingStore()
		// Единственная защита от лишних заказов: идемпотентности у бэка нет, каждый
		// checkout плодит новый заказ. Между вкладками она не работает — там человек
		// получит два заказа, из которых оплатит один, а второй протухнет сам.
		//
		// Молчаливый выход читался как сломанная кнопка: человек уже ждёт исход платежа,
		// и правильный ответ на клик — та же шторка ожидания, а не второй заказ. Шторка
		// сама решает, есть ли что показать: пока открыт виджет, она молчит.
		if (!RESTARTABLE_STATES.includes(billing.paymentState)) {
			billing.showWaiting()
			return
		}

		// После проверки выше: клик во время идущего платежа — не новая попытка.
		trackPaymentInit(source)
		const planId = resolvePlanId()
		billing.setState('loadingSdk')
		try {
			const order = await postCheckout(planId)

			const orderId = readInvoiceId(order.widget)
			if (!orderId) {
				// Без id заказ не опросить, то есть исход платежа не узнать никогда. Открыть
				// виджет в таком состоянии значило бы пустить в игру деньги, за которыми
				// потом некому проследить.
				console.error('[billing] в payload виджета нет invoiceId — наблюдать заказ нечем', order.widget)
				billing.fail(null)
				return
			}

			// Пишем заказ до открытия виджета: перезагрузки случаются ровно между.
			billing.trackOrder(orderId, order.expires_at)

			if (isAmountBroken(order.widget?.amount, usePlansStore().pro.priceMajor)) {
				console.error('[billing] сумма в payload выглядит как копейки — виджет не открыт', order.widget?.amount)
				billing.fail(null)
				return
			}

			if (!hasTerminalKey(order.widget)) {
				// Пустой ключ уже один раз маскировался под «виджет не открылся»: SDK в этом
				// случае молчит, а не ругается. Отказываем сами, чтобы причина была в логе.
				console.error('[billing] в payload нет ключа терминала — виджет не открыт', {
					publicId: order.widget?.publicId,
					publicTerminalId: order.widget?.publicTerminalId
				})
				billing.fail(null)
				return
			}

			billing.setState('widget')
			await openWidget(order.widget, orderId, order.expires_at)
		} catch (error) {
			handleCheckoutError(error, planId)
		}
	}

	async function openWidget(widget: WidgetPayload, id: string, expires: string | null): Promise<void> {
		const billing = useBillingStore()
		await openPaymentWidget(widget, {
			// Была попытка списания — деньги в игре, дальше истину знает только наш бэк.
			// attempted проносим до стора: из статуса заказа этот факт не выводится, бэк
			// отдаёт один pending и на свежий заказ, и на заказ с ушедшими деньгами.
			onAttempt: () => {
				void billing.watchOrder(id, expires, { attempted: true })
			},
			onClose: async ({ hadAttempt }) => {
				// Закрытие после попытки — не отмена: платёж мог пройти.
				if (hadAttempt) {
					void billing.watchOrder(id, expires, { attempted: true })
					return
				}
				// Ждём: cancel гасит заказ на бэке и только потом отпускает кнопку оплаты.
				await billing.cancel()
			}
		})
	}

	function handleCheckoutError(error: unknown, planId: string): void {
		const billing = useBillingStore()
		const api = error instanceof ApiError ? error : null

		if (api?.code === BILLING_ERROR_CODES.PLAN_NOT_FOUND) {
			// Отправленный идентификатор бэк не знает: скорее всего он ждёт другое поле тарифа
			// (см. PLAN_ID_FIELD). Без этой записи в логе интеграция отлаживается вслепую.
			console.error(`[billing] бэк не знает plan_id «${planId}» (поле ${PLAN_ID_FIELD})`)
			billing.fail(null)
			return
		}

		if (api?.code === BILLING_ERROR_CODES.SUBSCRIPTION_ALREADY_ACTIVE || api?.status === 409) {
			// Человек уже получил то, что покупает: показывать ошибку абсурдно.
			void useUsageStore().fetchUsage()
			usePaywallStore().close()
			billing.reset()
			return
		}
		if (api?.code === BILLING_ERROR_CODES.GUEST_NOT_ALLOWED || api?.status === 403) {
			// Не должно случаться: гейт не пускает гостя до оплаты.
			billing.reset()
			useCommonStore().openLoginModal()
			return
		}

		console.warn('[billing] заказ не создан', error)
		billing.fail(null)
	}

	return { startCheckout }
}
